import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TopicFilter } from '@components/topics/TopicFilter';
import { TopicCard } from '@components/topics/TopicCard';
import { Button } from '@components/common/Button';
import { SkeletonCard } from '@components/common/Loading';
import { FullScreenLoading } from '@components/common/FullScreenLoading';
import { useGetTopicsQuery, useGetAIRecommendationsMutation, useAnalyzeTopicMutation, } from '@services/topicsApi';
import { useCreateProjectMutation } from '@services/projectsApi';
import { useToast } from '@hooks/useToast';
export default function TopicSelectionPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromWorkId = searchParams.get('from');
    const { success: showSuccess, error: showError } = useToast();
    const [filters, setFilters] = useState({
        category: 'all',
        genre: [],
        duration: 'all',
    });
    const [showAIRecommendations, setShowAIRecommendations] = useState(false);
    const [selectedTopicAnalysis, setSelectedTopicAnalysis] = useState(null);
    const [generatingScript, setGeneratingScript] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationMessage, setGenerationMessage] = useState('');
    const { data, isLoading, error } = useGetTopicsQuery({
        category: filters.category !== 'all' ? filters.category : undefined,
        genre: filters.genre.length > 0 ? filters.genre : undefined,
        duration: filters.duration !== 'all' ? filters.duration : undefined,
        page: 1,
        pageSize: 12,
    });
    const [getAIRecommendations, { data: aiRecommendations, isLoading: isLoadingAI }] = useGetAIRecommendationsMutation();
    const [analyzeTopic, { isLoading: isAnalyzing }] = useAnalyzeTopicMutation();
    const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setShowAIRecommendations(false);
    };
    // AI剧本生成功能 - 优化版本
    const handleAIScriptGeneration = async () => {
        try {
            setGeneratingScript(true);
            setGenerationProgress(10);
            setGenerationMessage('正在创建项目...');
            let topic = 'AI智能创作剧本';
            let style = '剧情';
            if (filters.category === 'comedy') {
                style = '喜剧';
                topic = '一个轻松搞笑的';
            }
            else if (filters.category === 'suspense') {
                style = '悬疑';
                topic = '一个扣人心弦的悬疑';
            }
            else if (filters.category === 'scifi') {
                style = '科幻';
                topic = '一个充满想象力的科幻';
            }
            else if (filters.category === 'fantasy') {
                style = '奇幻';
                topic = '一个奇幻冒险';
            }
            else if (filters.category === 'action') {
                style = '动作';
                topic = '一个激动人心的动作';
            }
            if (filters.genre.length > 0) {
                topic += filters.genre.join('、') + '题材的';
            }
            if (filters.duration === 'short') {
                topic += '短视频故事（1-3分钟）';
            }
            else if (filters.duration === 'medium') {
                topic += '中等长度故事（3-10分钟）';
            }
            else if (filters.duration === 'long') {
                topic += '长视频故事（10分钟以上）';
            }
            else {
                topic += '故事';
            }
            console.log('AI生成主题:', topic, '风格:', style);
            const project = await createProject({
                title: `AI智能创作 - ${style}`,
                description: topic,
            }).unwrap();
            console.log('项目创建成功:', project.id);
            setGenerationProgress(30);
            setGenerationMessage('AI正在创作剧本...');
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/projects/${project.id}/generate-script`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    topic: topic,
                    style: style
                })
            });
            if (!response.ok) {
                throw new Error('AI生成剧本失败');
            }
            setGenerationProgress(80);
            const result = await response.json();
            console.log('AI生成的剧本:', result.script);
            setGenerationProgress(100);
            setGenerationMessage('剧本生成成功！正在跳转...');
            // 等待1秒让用户看到完成消息
            setTimeout(() => {
                navigate(`/project/${project.id}/script`);
            }, 1000);
        }
        catch (err) {
            console.error('AI生成错误:', err);
            setGeneratingScript(false);
            showError('AI生成失败，请重试');
        }
    };
    const handleAnalyze = async (topicId) => {
        try {
            const analysis = await analyzeTopic(topicId).unwrap();
            setSelectedTopicAnalysis({ topicId, analysis });
        }
        catch (err) {
            showError('分析失败，请重试');
        }
    };
    const handleSelectTopic = async (topicId) => {
        try {
            const topic = data?.items.find((t) => t.id === topicId);
            if (!topic)
                return;
            showSuccess('正在创建项目...');
            const project = await createProject({
                title: `基于《${topic.title}》的创作`,
                description: topic.synopsis,
                topicId: topic.id,
            }).unwrap();
            showSuccess('项目创建成功！正在生成剧本...');
            try {
                const response = await fetch(`/api/projects/${project.id}/generate-script`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ topicId: topic.id })
                });
                if (response.ok) {
                    const result = await response.json();
                    showSuccess('AI剧本生成成功！');
                    console.log('AI生成的剧本:', result.script);
                }
                else {
                    console.warn('AI剧本生成失败，使用模板');
                }
            }
            catch (aiError) {
                console.error('AI剧本生成错误:', aiError);
            }
            navigate(`/project/${project.id}/script`);
        }
        catch (err) {
            showError('创建项目失败，请重试');
        }
    };
    const topicsToDisplay = showAIRecommendations && aiRecommendations ? aiRecommendations : data?.items;
    return (_jsxs(_Fragment, { children: [generatingScript && (_jsx(FullScreenLoading, { message: generationMessage, progress: generationProgress, subMessage: "AI\u6B63\u5728\u4E3A\u60A8\u521B\u4F5C\u7CBE\u5F69\u5267\u672C\uFF0C\u9884\u8BA1\u9700\u898130-60\u79D2" })), _jsx("div", { className: "mx-auto max-w-[1440px] px-6 py-8", children: _jsxs("div", { className: "flex gap-6", children: [_jsx("aside", { className: "w-64 flex-shrink-0", children: _jsx(TopicFilter, { onFilterChange: handleFilterChange }) }), _jsxs("div", { className: "flex-1 space-y-6", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-gray-100", children: "\u9009\u62E9\u9009\u9898" }), _jsx("p", { className: "text-sm text-gray-400", children: fromWorkId ? '为您推荐相似的选题' : '浏览热门选题，开始您的创作' })] }), _jsx(Button, { variant: "secondary", onClick: handleAIScriptGeneration, loading: generatingScript, disabled: generatingScript, children: "\u2728 AI\u5267\u672C\u751F\u6210" })] }), isLoading && (_jsx("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3", children: Array.from({ length: 6 }).map((_, i) => (_jsx(SkeletonCard, {}, i))) })), error && (_jsx("div", { className: "rounded-lg bg-red-50 p-8 text-center", children: _jsx("p", { className: "text-red-600", children: "\u52A0\u8F7D\u5931\u8D25\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5" }) })), topicsToDisplay && topicsToDisplay.length > 0 && (_jsx("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3", children: topicsToDisplay.map((topic) => (_jsx(TopicCard, { topic: topic, onSelect: handleSelectTopic, onAnalyze: handleAnalyze }, topic.id))) })), topicsToDisplay && topicsToDisplay.length === 0 && (_jsx("div", { className: "rounded-lg bg-dark-600 border border-gray-800 p-12 text-center", children: _jsx("p", { className: "text-gray-400", children: "\u6682\u65E0\u9009\u9898" }) }))] })] }) })] }));
}
